import {
  createContext,
  useContext,
  useState,
} from "react";
import { http } from "../services/http";
import { clearAuthSession, TOKEN_KEY, USER_KEY } from "../lib/authStorage";

export const AuthContext = createContext({});

// Sessões abertas antes desta versão não têm @auth:user gravado; o e-mail
// ainda pode ser lido do payload do JWT, sem dependência nova. O nome não
// está no token — quem o quiser usa as iniciais do e-mail (lib/initials).
function userFromToken(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.email ? { name: null, email: payload.email } : null;
  } catch {
    return null;
  }
}

function readStoredUser() {
  try {
    const stored = JSON.parse(localStorage.getItem(USER_KEY));
    if (stored?.email) return stored;
  } catch {
    /* JSON corrompido: o token abaixo ainda serve. */
  }
  return userFromToken(localStorage.getItem(TOKEN_KEY));
}

export function AuthProvider({ children }) {
  // Inicialização síncrona, não em efeito: com um passo assíncrono, o primeiro
  // render de um deep link com sessão válida veria signed=false e a guarda de
  // rota redirecionaria para o login antes do efeito rodar.
  const [signed, setSigned] = useState(() => Boolean(localStorage.getItem(TOKEN_KEY)));
  const [user, setUser] = useState(readStoredUser);

  const signIn = async (email, password) => {
    try {
      const response = await http.post(`/login`, {
        email,
        password,
      });

      if (response?.data?.data?.token) {
        const { token, name } = response.data.data;
        const signedUser = { name: name ?? null, email: response.data.data.email ?? email };

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(signedUser));
        setUser(signedUser);
        setSigned(true);
      } else {
        throw new Error("Token não recebido.");
      }
    } catch (error) {
      throw new Error("Erro durante o login", error);
    }
  };

  const signOut = async () => {
    clearAuthSession();
    setUser(null);
    setSigned(false);
  };

  return (
    <AuthContext.Provider value={{ signed, user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
